import {
  connectSocial,
  getBrand,
  getSocials,
  removeSocialMediaAccount,
  axiosInstance,
} from "api";
import { useMemo, useState } from "react";
import { platformNameMap, showAlert } from "utils/utils";
import { useEffect } from "react";

const useManageChannels = (brandId) => {
  const [selectedChannel, setSelectedChannel] = useState();
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState({});
  const [socials, setSocials] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [showRefreshConnectionModal, setShowRefreshConnectionModal] =
    useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);

  const platformList = useMemo(
    () => socials.map((item) => item.platform),
    [socials]
  );

  const fetchBrand = async () => {
    try {
      const brandRes = await getBrand(brandId);
      if (brandRes?.data?.status === true) setBrand(brandRes.data.data);
    } catch (e) {
      console.log(e);
    }
  };

  const loadSocials = async () => {
    const { search } = window.location;

    const error = new URLSearchParams(search).get("error");
    const connected_platform = new URLSearchParams(search).get("platform");
    const connected_from_post = new URLSearchParams(search).get(
      "connected_from_post"
    );

    try {
      await fetchBrand();
      const socialsRes = await getSocials(brandId);

      if (socialsRes?.data?.status === true) {
        setSocials(socialsRes?.data?.data?.socials);
      }
      // dont display alert when connected from create post
      if (connected_from_post) {
        setLoading(false);
        return;
      }

      // show alert after checking brand details
      if (connected_platform && connected_platform === "Youtube") {
        setShowYoutubeModal(true);
      } else if (error) {
        showAlert({
          msg: error,
          type: "danger",
        });
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      showAlert({
        msg: error,
        type: "Failed to fetch brand details",
      });
    }
  };

  const removeSocial = async () => {
    if (!selectedChannel) {
      return;
    }

    const { platform, id } = selectedChannel;

    const data = {
      id,
      brandId,
      platform,
    };

    try {
      const res = await removeSocialMediaAccount(data);

      if (res.data.status === true) {
        await loadSocials();

        showAlert({
          type: "success",
          msg: `${platformNameMap[platform]} account removed`,
        });
      } else {
        showAlert({
          type: "danger",
          msg: res.data?.message,
        });
      }
    } catch (e) {
      showAlert({
        type: "danger",
        msg: e.response?.data?.message || `Error. Could not remove platform`,
      });
      console.log(e);
    }
  };

  const handleRemoveChannel = (channel) => {
    setSelectedChannel(channel);
    toggleShowOptions("");
    setTimeout(() =>
      document.querySelector("#confirmPlatformRemoval").classList.add("show")
    );
  };

  const toggleShowOptions = (id) => {
    document.querySelectorAll(".connected .options").forEach((el) => {
      if (el.dataset.id === id) {
        el.classList.toggle("open");
      } else {
        el.classList.remove("open");
      }
    });
  };

  const handleTokenRefresh = async (channel) => {
    const { platform } = channel;
    if (platform === "youtube") return;

    try {
      const res = await axiosInstance.get(`${platform}/${brandId}/refresh`);

      if (res?.data?.status === true) {
        const redirectUrl = res.data?.redirect;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }
        if (channel.platform === "tiktok") {
          window.location.reload();
        }
        showAlert({
          msg: res.data?.message,
          type: "success",
        });
      } else {
        showAlert({
          msg: res.data?.message,
          type: "danger",
        });
      }
    } catch (e) {
      console.log(e);
      showAlert({
        msg:
          e.response?.data?.message ||
          "An error occurred. Could not refresh connection.",
        type: "danger",
      });
    }

    loadSocials();
  };

  const handleConnectPlatform = async (platform, direct = false) => {
    setConnecting(true);
    if (!platform) {
      setConnecting(false);
      return;
    }
    try {
      const res = await connectSocial(platform, direct);
      if (res?.data?.status === true) {
        const redirectUrl = res.data.redirect;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }
      } else {
        showAlert({
          msg: res.data?.message,
          type: "danger",
        });
      }
      setConnecting(false);
    } catch (e) {
      setConnecting(false);
      showAlert({
        msg:
          e.response?.data?.message ||
          "An error occurred. Could not complete the request.",
        type: "danger",
      });
    }

    setConnecting(false);
  };

  useEffect(() => {
    loadSocials();
    // eslint-disable-next-line
  }, []);

  return {
    fetchBrand,
    loadSocials,
    socials,
    platformList,
    removeSocial,
    handleRemoveChannel,
    toggleShowOptions,
    handleTokenRefresh,
    handleConnectPlatform,
    loading,
    brand,
    connecting,
    showRefreshConnectionModal,
    setShowRefreshConnectionModal,
    showYoutubeModal,
    setShowYoutubeModal,
    setSelectedChannel,
  };
};

export default useManageChannels;
